import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Check, Globe } from 'lucide-react'
import { useI18n, SUPPORTED_LANGUAGES } from '../i18n'

// Orella brand colors
const ORELLA_BLUE = '#46A8F9'
const ICE_BLUE = '#E9F8FF'
const NAVY = '#15354F'
const LIGHT_BLUE = '#A7D9FC'
const GREEN = '#66D170'

// Bootstrap labels — hardcoded per-language because the picker must read
// correctly even before the user has committed to a language. Rendered in the
// CURRENT (already-applied) language; selecting applies immediately + closes,
// so there's no preview state to translate live anymore.
const HEADER_TRANSLATIONS = {
  en: 'Your language', es: 'Tu idioma', zh: '你的语言',
  vi: 'Ngôn ngữ', ko: '언어 선택', fr: 'Votre langue',
  ar: 'لغتك', ru: 'Ваш язык', pt: 'Seu idioma',
  tl: 'Iyong Wika', hi: 'आपकी भाषा'
}

const AVAILABLE_TRANSLATIONS = {
  en: 'Available', es: 'Disponible', zh: '可用', vi: 'Có sẵn',
  ko: '사용 가능', fr: 'Disponible', ar: 'متاح', ru: 'Доступно',
  pt: 'Disponível', tl: 'Magagamit', hi: 'उपलब्ध'
}

const SOON_TRANSLATIONS = {
  en: 'Soon', es: 'Pronto', zh: '即将', vi: 'Sắp có',
  ko: '곧', fr: 'Bientôt', ar: 'قريباً', ru: 'Скоро',
  pt: 'Em breve', tl: 'Malapit na', hi: 'जल्द'
}

/**
 * Language Selector — v239.13.2 (dropdown redesign, "Option B")
 *
 * Top-right anchored dropdown that drops in below the status bar. Replaces the
 * prior bottom-sheet modal. Keeps the exact <LanguageSelector isOpen onClose />
 * contract so all 11 call sites (Home pill, hamburger menu, auth, etc.) work
 * unchanged — when opened from the hamburger, the drawer slides out first and
 * this drops in from the top-right.
 *
 * Behavior: tap an ACTIVE language = apply immediately + close (no Continue
 * button). comingSoon languages are inert and tagged "Soon". Active-but-
 * unselected languages are tagged "Available" (green); the selected one shows a
 * brand check on a light-blue row.
 */
export default function LanguageSelector({ isOpen, onClose }) {
  const { language, setLanguage } = useI18n()

  // Lock body scroll while open. Mount == open (we return null when closed),
  // so the open animations fire on mount with no class toggling needed.
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
      return () => { document.body.style.overflow = '' }
    }
  }, [isOpen])

  if (!isOpen) return null

  const handleSelect = (lang) => {
    if (lang.comingSoon) return
    setLanguage(lang.code)
    onClose()
  }

  const headerLabel = HEADER_TRANSLATIONS[language] || HEADER_TRANSLATIONS.en
  // ★ v242.151.0 LANG-PILL-NATIVE-1 — a pill speaks for the row it labels, not
  //   for the language you are already reading. These were keyed on `language`
  //   (the CURRENT UI language), so all eleven translations existed and exactly
  //   one was ever on screen: with English selected every row said "Soon".
  const labelFor = (map, code) => map[code] || map.en

  return createPortal(
    <div className="fixed inset-0 z-[9999]">
      <style>{`
        @keyframes langDdOpen {
          from { opacity: 0; transform: translateY(-8px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes langRowIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes langScrimIn { from { opacity: 0; } to { opacity: 1; } }
        @media (prefers-reduced-motion: reduce) {
          .lang-dd, .lang-row { animation: none !important; opacity: 1 !important; transform: none !important; }
        }
      `}</style>

      {/* Click-catcher / subtle dim — taps outside close the dropdown */}
      <div
        className="absolute inset-0"
        style={{ background: 'rgba(8,20,33,0.16)', animation: 'langScrimIn 0.2s ease' }}
        onClick={onClose}
      />

      {/* Dropdown panel — anchored top-right, below the status bar/header */}
      <div
        className="lang-dd absolute bg-white"
        role="listbox"
        aria-label={headerLabel}
        style={{
          top: 'calc(env(safe-area-inset-top, 8px) + 56px)',
          right: 12,
          width: 'min(264px, calc(100vw - 24px))',
          maxHeight: 'calc(100dvh - env(safe-area-inset-top, 8px) - 120px)',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 18,
          border: '1px solid #E8EEF4',
          boxShadow: '0 22px 50px rgba(8,20,33,0.34)',
          overflow: 'hidden',
          transformOrigin: 'top right',
          animation: 'langDdOpen 0.22s cubic-bezier(.2,.8,.3,1) both',
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between flex-shrink-0"
          style={{ padding: '11px 14px 7px' }}
        >
          <span
            className="font-extrabold uppercase"
            style={{ fontSize: 9.5, letterSpacing: '0.1em', color: '#9fb3c4' }}
          >
            {headerLabel}
          </span>
          <div
            className="rounded-md flex items-center justify-center flex-shrink-0"
            style={{ width: 22, height: 22, backgroundColor: ICE_BLUE }}
          >
            <Globe size={12} style={{ color: ORELLA_BLUE }} />
          </div>
        </div>

        {/* Language rows */}
        <div className="overflow-y-auto" style={{ flex: '1 1 auto', minHeight: 0, paddingBottom: 6 }}>
          {SUPPORTED_LANGUAGES.map((lang, i) => {
            const isSelected = language === lang.code
            const isSoon = !!lang.comingSoon

            return (
              <button
                key={lang.code}
                type="button"
                role="option"
                aria-selected={isSelected}
                disabled={isSoon}
                onClick={() => handleSelect(lang)}
                className="lang-row w-full flex items-center gap-2 text-left transition-colors"
                style={{
                  padding: '9px 14px',
                  cursor: isSoon ? 'default' : 'pointer',
                  backgroundColor: isSelected ? 'rgba(70,168,249,0.10)' : 'transparent',
                  animation: `langRowIn 0.34s ease both`,
                  animationDelay: `${0.06 + i * 0.045}s`,
                }}
              >
                <span
                  className="flex-1"
                  dir={lang.rtl ? 'rtl' : undefined}
                  style={{
                    fontSize: 13.5,
                    fontWeight: isSoon ? 600 : 700,
                    color: isSelected ? ORELLA_BLUE : isSoon ? '#8aa0b2' : NAVY,
                  }}
                >
                  {lang.nativeName}
                </span>

                {isSelected ? (
                  <Check size={16} strokeWidth={3} style={{ color: ORELLA_BLUE, flexShrink: 0 }} />
                ) : isSoon ? (
                  <span
                    className="font-extrabold flex-shrink-0"
                    style={{ fontSize: 9, color: '#9fb3c4', backgroundColor: '#F1F5F9', padding: '2px 7px', borderRadius: 999 }}
                  >
                    {labelFor(SOON_TRANSLATIONS, lang.code)}
                  </span>
                ) : (
                  <span
                    className="font-extrabold flex-shrink-0"
                    style={{ fontSize: 9, color: '#2f8f3e', backgroundColor: 'rgba(102,209,112,0.20)', padding: '2px 7px', borderRadius: 999 }}
                  >
                    {labelFor(AVAILABLE_TRANSLATIONS, lang.code)}
                  </span>
                )}
              </button>
            )
          })}
        </div>
      </div>
    </div>,
    document.body
  )
}

/**
 * Language Button Component — typographic pill (globe + native language name).
 * Unchanged: this is the Home-screen trigger; the dropdown anchors top-right
 * just beneath it.
 */
export function LanguageButton({ onClick }) {
  const { currentLanguage } = useI18n()

  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm rounded-full transition-colors"
      style={{
        border: `1.5px solid ${LIGHT_BLUE}`,
        backgroundColor: ICE_BLUE,
      }}
    >
      <Globe size={13} style={{ color: ORELLA_BLUE }} />
      <span className="font-bold text-[12.5px]" style={{ color: NAVY }}>
        {currentLanguage.nativeName}
      </span>
    </button>
  )
}
